import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?inline";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#A5C831" },
      { name: "author", content: "Elektriker-Elektriker ApS" },
      { name: "robots", content: "index, follow" },
      { property: "og:site_name", content: "Elektriker-Elektriker" },
      { property: "og:locale", content: "da_DK" },
      { title: "Elektriker København & omegn | Autoriseret elektriker" },
      { property: "og:title", content: "Elektriker København & omegn | Autoriseret elektriker" },
      { name: "twitter:title", content: "Elektriker København & omegn | Autoriseret elektriker" },
      { name: "description", content: "Autoriseret elektriker i København og hele Storkøbenhavn. Vi dækker alt fra Amager til Østerbro og Hellerup." },
      { property: "og:description", content: "Autoriseret elektriker i København og hele Storkøbenhavn. Vi dækker alt fra Amager til Østerbro og Hellerup." },
      { name: "twitter:description", content: "Autoriseret elektriker i København og hele Storkøbenhavn. Vi dækker alt fra Amager til Østerbro og Hellerup." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/I9i7cJjfgiONHj142RRiXL6M3Md2/social-images/social-1781601546631-1.V1.-SAK-Electric-Nov-22.6039-scaled.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/I9i7cJjfgiONHj142RRiXL6M3Md2/social-images/social-1781601546631-1.V1.-SAK-Electric-Nov-22.6039-scaled.webp" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "icon", href: "/favicon.png", type: "image/png" },
    ],
    scripts: [
      {
        src: "https://app.weply.chat/widget/60e285d7f8587283a3833fb2c1b25039",
        async: true,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Electrician",
          name: "Elektriker-Elektriker ApS",
          url: "/",
          telephone: "+4528700028",
          email: "el@elektriker-elektriker.dk",
          image: "/og-image.jpg",
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Østersøgade 106A",
            postalCode: "2100",
            addressLocality: "København Ø",
            addressCountry: "DK",
          },
          areaServed: [
            "København", "Frederiksberg", "Hellerup", "Charlottenlund",
            "Gentofte", "Gladsaxe", "Nordsjælland", "Amager", "Dragør",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="da">
      <head>
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
